import React, { useCallback, useState } from 'react';

import { useSessionStore } from '@features/auth/store/useSessionStore';

import useWizardStore from '@features/wizard/store/useWizardStore';
import useSessionsViewModel from '@features/wizard/hooks/useSessionsViewModel';
import type { ContactListDto } from '@features/wizard/types/wizard.types';

import SessionsList from './SessionsList';
import NewListPreview from './NewListPreview';

const SessionsTabPanel: React.FC = () => {
  const currentUser = useSessionStore((state) => state.currentUser);
  const organizationId = String(currentUser?.organizationId ?? '').trim();

  const {
    sessionsTab,
    toggleSelectedSessionId,
    toggleSessionsFilter,
    setSessionsActiveFilters,
    setNewListName,
    setSessionsSearchQuery,
    setSessionsCurrentPage,
    setSessionsPageSize,
    setContactList,
    selectedContactList,
  } = useWizardStore();

  const {
    sessions,
    isLoadingSessions,
    statusCounts,
    isLoadingCounts,
    paginatedContacts,
    totalFilteredContacts,
    isLoadingPreview,
    hasSelection,
    canProceed,
    createListFromSelection,
  } = useSessionsViewModel();

  const [isSavingList, setIsSavingList] = useState(false);

  /** When the user clicks Save list we materialize the filtered selection into a
   * real ContactListDto and store it on the wizard so step 2's Next behaves
   * exactly like CSV/Outreach.
   */
  const handleSaveList = useCallback(async () => {
    setIsSavingList(true);
    const newListId = await createListFromSelection();
    if (newListId !== null) {
      const newList: ContactListDto = {
        id: newListId,
        organizationId,
        title: sessionsTab.newListName.trim(),
        type: 'SESSIONS',
        contactsCount: totalFilteredContacts,
      };
      setContactList(newList);
    }
    setIsSavingList(false);
  }, [createListFromSelection, organizationId, sessionsTab.newListName, totalFilteredContacts, setContactList]);

  const savedListId = selectedContactList?.type === 'SESSIONS'
    ? selectedContactList.id
    : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(280px,_0.8fr)_1.4fr] gap-6 min-w-0">
      <SessionsList
        sessions={sessions}
        selectedSessionIds={sessionsTab.selectedSessionIds}
        searchQuery={sessionsTab.searchQuery}
        onSearchChange={setSessionsSearchQuery}
        onToggleSession={toggleSelectedSessionId}
        isLoading={isLoadingSessions}
      />

      <NewListPreview
        hasSelection={hasSelection}
        selectedSessionsCount={sessionsTab.selectedSessionIds.length}
        totalFilteredContacts={totalFilteredContacts}
        counts={statusCounts}
        activeFilters={sessionsTab.activeFilters}
        onToggleFilter={toggleSessionsFilter}
        onClearAllFilters={() => {
          setSessionsActiveFilters([]);
        }}
        isLoadingCounts={isLoadingCounts}
        newListName={sessionsTab.newListName}
        onNewListNameChange={setNewListName}
        paginatedContacts={paginatedContacts}
        currentPage={sessionsTab.currentPage}
        pageSize={sessionsTab.pageSize}
        onPageChange={setSessionsCurrentPage}
        onPageSizeChange={setSessionsPageSize}
        isLoadingPreview={isLoadingPreview}
        canSaveList={canProceed}
        isSavingList={isSavingList}
        savedListId={savedListId}
        onSaveList={() => {
          void handleSaveList();
        }}
      />
    </div>
  );
};

export default SessionsTabPanel;
